<template>
  <!-- MYAC-918 — one conversation in a list: its subject on one line; under it, at
       most one state; when it was last touched on the right. Compact on purpose: a
       person's list gets long. Ported from the mockup (front PR 868). The pencil
       renames in place (Sébastien, 2026-10-02): the title turns into a field. -->
  <!-- MYAC-1269 (Aymeric, 2026-10-27) — two lines, always: the subject on the
       first, nearly the full width; under it the state on the left (« Nouvelle
       réponse », « J'y travaille »…) and the share mark with the time on the
       right. The height never moves when a state comes or goes, and « ⋮ » sits
       inside the row's block. -->
  <div class="row-wrap" :class="{ renamable: canRename, current, editing }" data-testid="conversation-row">
    <div v-if="editing" class="conversation-row">
      <input ref="input" v-model="draft" class="rename-input" maxlength="120"
        :aria-label="t('conversations.rename_label')" data-testid="conversation-row-rename-input"
        @keydown.enter.prevent="commit" @keydown.esc.prevent="editing = false" @blur="commit" />
    </div>
    <button v-else type="button" class="conversation-row" :class="{ current }"
      :aria-current="current ? 'true' : undefined" @click="emit('select')">
      <span class="title">{{ row.title || t('conversations.untitled') }}</span>
      <span class="sub">
        <!-- the time and the share mark on the left, the state at the end -->
        <span class="when">{{ when }}</span>
        <!-- one of mine that others can read: said on the row, so « Les vôtres »
             never reads as « visible by me alone ». -->
        <i v-if="sharedByMe" class="pi pi-users shared-mark" :title="t('conversations.row.shared')"
          :aria-label="t('conversations.row.shared')" data-testid="conversation-row-shared" />
        <!-- someone else's, shared with the whole project (not named to me). -->
        <i v-else-if="!mine && row.visibility === 'project'" class="pi pi-globe shared-mark"
          :title="t('conversations.row.shared_project')" :aria-label="t('conversations.row.shared_project')"
          data-testid="conversation-row-project" />
        <span v-if="state === 'working'" class="state working">
          <ActivityCompass state="working" :size="13" />{{ t('conversations.state.working') }}
        </span>
        <!-- MYAC-1269 — on a shared conversation the new answer came from someone
             else's question: « Marc a écrit » instead of « Nouvelle réponse ». -->
        <span v-else-if="state === 'answer' && wroteBy" class="state answer" data-testid="conversation-row-wrote-by">
          <i class="dot" /><span class="who"><UserName :uid="wroteBy" /> {{ t('conversations.state.wrote') }}</span>
        </span>
        <span v-else-if="state === 'answer'" class="state answer"><i class="dot" />{{ t('conversations.state.answer') }}</span>
        <span v-else-if="state === 'proposal'" class="state proposal"><i class="dot" />{{ t('conversations.state.proposal') }}</span>
      </span>
    </button>
    <span v-if="!editing && menuItems.length" class="row-actions">
      <button type="button" class="row-action" :aria-label="t('conversations.more_actions')"
        aria-haspopup="true" data-testid="conversation-row-menu" @click.stop="menu?.toggle($event)">
        <i class="pi pi-ellipsis-v" aria-hidden="true" />
      </button>
      <Menu ref="menu" :model="menuItems" popup />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ConversationRow, ConversationState } from '@/stores/conversations'
import type { ConversationRight } from '@/stores/chatbot'
import Menu from 'primevue/menu'
import ActivityCompass from '@/components/chat/ActivityCompass.vue'
import UserName from '@/components/shared/users/UserName.vue'
import { mayRename, whenParts } from './conversationView'

const props = withDefaults(defineProps<{
  row: ConversationRow
  state: ConversationState
  current?: boolean
  now?: Date
  /** MYAC-1664 — the person's right on it: the owner renames and shares, a writer renames. */
  right?: ConversationRight
  /** MYAC-1269 — who sent the turn behind the new answer, when it was not the person. */
  wroteBy?: string | null
}>(), { current: false, now: undefined, right: null, wroteBy: null })

const emit = defineEmits<{ select: []; rename: [title: string]; share: [] }>()
const { t, d } = useI18n()

const mine = computed(() => props.right === 'owner')
const canRename = computed(() => mayRename(props.right))
const sharedByMe = computed(() => mine.value && props.row.visibility !== 'private')

const menu = ref<InstanceType<typeof Menu> | null>(null)
// The owner renames and shares; a writer renames (MYAC-1664); the commands refuse anyone else.
const menuItems = computed(() => [
  ...(canRename.value ? [{ label: t('conversations.rename'), icon: 'pi pi-pencil', command: () => { void startEdit() } }] : []),
  ...(mine.value ? [{ label: t('conversations.share_menu'), icon: 'pi pi-users', command: () => emit('share') }] : []),
])

const editing = ref(false)
const draft = ref('')
const input = ref<HTMLInputElement | null>(null)
async function startEdit(): Promise<void> {
  draft.value = props.row.title
  editing.value = true
  await nextTick()
  input.value?.select()
}
function commit(): void {
  if (!editing.value) return
  editing.value = false
  const clean = draft.value.replace(/\s+/g, ' ').trim()
  if (clean && clean !== props.row.title) emit('rename', clean)
}

const when = computed(() => {
  const w = whenParts(props.row.updatedAt, props.now ?? new Date())
  switch (w.key) {
    case 'now': return t('conversations.when.now')
    case 'minutes': return t('conversations.when.minutes', { n: w.n }, w.n)
    case 'hours': return t('conversations.when.hours', { n: w.n }, w.n)
    case 'yesterday': return t('conversations.when.yesterday')
    case 'weekday': return d(w.date, { weekday: 'long' })
    default: return d(w.date, { day: 'numeric', month: 'short' })
  }
})
</script>

<style scoped>
/* The row's block carries the hover and « current » colours, so « ⋮ » is inside it. */
.row-wrap { position: relative; display: flex; align-items: center; gap: 2px; border-radius: 6px; }
.row-wrap:hover { background: var(--p-surface-50); }
.row-wrap.current { background: var(--p-primary-50); }
.row-wrap.editing { background: var(--p-primary-50); }
.conversation-row {
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; padding: 0;
  border: 0; background: transparent; text-align: left; font: inherit; color: var(--p-surface-900); cursor: pointer;
}
.conversation-row:focus-visible { outline: var(--focus-outline, 2px solid var(--p-primary-color)); outline-offset: 2px; border-radius: 4px; }
.rename-input {
  flex: 1; min-width: 0; font: inherit; font-size: 14px; font-weight: 500; border: 1px solid var(--p-primary-300);
  border-radius: 6px; padding: 4px 8px; outline: none; background: var(--p-surface-0);
}
.title { font-size: 14px; font-weight: 500; line-height: 1.35; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sub { display: flex; align-items: center; gap: 6px; min-width: 0; min-height: 1.4em; font-size: 12px; line-height: 1.4; }
.state { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; min-width: 0; overflow: hidden; }
.state.working, .state.answer { color: var(--p-primary-color); }
/* Orange is for the one thing that waits for the person; dark text keeps it readable. */
.state.proposal { color: var(--p-orange-900, #7a4b00); }
.who { overflow: hidden; text-overflow: ellipsis; }
.dot { width: 7px; height: 7px; border-radius: 9999px; display: inline-block; flex-shrink: 0; }
.answer .dot { background: var(--p-primary-color); }
.proposal .dot { background: var(--p-orange-300, #fbbe5e); }
.shared-mark { font-size: 11px; color: var(--p-surface-500); }
.when { flex-shrink: 0; font-size: 12px; color: var(--p-surface-500); white-space: nowrap; }
.row-actions { flex-shrink: 0; display: inline-flex; align-self: center; }
.row-action {
  width: 24px; height: 28px; border: 0; border-radius: 6px; background: transparent; color: var(--p-surface-400);
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
}
.row-action:hover, .row-action:focus-visible { color: var(--p-primary-color); background: var(--p-primary-100); }
</style>
